/**
 * /reset-password — enter code + new password.
 *
 * Two entry paths:
 *   1. Classic /forgot-password flow: `signIn.status` is still
 *      `needs_first_factor` here, so we collect the emailed 6-digit
 *      code and call `signIn.attemptFirstFactor({ strategy:
 *      'reset_password_email_code', code })` before resetting.
 *   2. Breach-detected sign-in: Clerk rejected an otherwise-correct
 *      password and `signIn.status` is *already* `needs_new_password`
 *      — no code was ever sent, so the code field is hidden and we
 *      go straight to `signIn.resetPassword({ password })`. The
 *      `reason` search param (from `signIn.firstFactorVerification.error`)
 *      explains why the user landed here.
 *
 * Both paths branch on the final status:
 *   - `complete` → activate session → `/`
 *   - `needs_second_factor` → `/two-factor?flow=sign-in`
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import { activateSession, resetPassword } from '@/lib/auth';
import { useSignIn } from '@clerk/react/legacy';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_auth/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search): { reason?: string } => ({
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
});

function ResetPasswordPage(): ReactElement {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();
  const { reason } = Route.useSearch();

  const needsCode = signIn?.status !== 'needs_new_password';

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !signIn || !setActive) return;

    if (password !== confirm) {
      setFieldErrors({ confirm: 'Passwords do not match.' });
      return;
    }
    if (needsCode && code.length !== 6) {
      setFieldErrors({ code: 'Enter the 6-digit code from your email.' });
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    const result = await resetPassword({ signIn, code: needsCode ? code : undefined, password });

    if (result.kind === 'error') {
      setFieldErrors(result.fieldErrors);
      setFormError(
        result.fieldErrors.code || result.fieldErrors.password ? undefined : result.formError,
      );
      setSubmitting(false);
      return;
    }

    if (result.kind === 'needs_second_factor') {
      navigate({ to: '/two-factor', search: { flow: 'sign-in' } });
      return;
    }

    await activateSession({ setActive, sessionId: result.sessionId, redirectUrl: '/' });
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-6 text-sm text-[var(--color-mute)]">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Reset password</h1>
        <p className="text-sm text-[var(--color-mute)]">
          {needsCode
            ? 'Enter the code from your email and choose a new password.'
            : 'Choose a new password to finish signing in.'}
        </p>
      </header>

      {reason && (
        <Alert variant="warning">
          <AlertTitle>Password reset required</AlertTitle>
          <AlertDescription>{reason}</AlertDescription>
        </Alert>
      )}

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {needsCode && (
          <Field>
            <FieldLabel htmlFor="code">Verification code</FieldLabel>
            <OTPField length={6} value={code} onValueChange={setCode}>
              {Array.from({ length: 6 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP slots
                <OTPFieldInput key={i} />
              ))}
            </OTPField>
            {fieldErrors.code && <FieldError>{fieldErrors.code}</FieldError>}
          </Field>
        )}

        <Field>
          <FieldLabel htmlFor="password">New password</FieldLabel>
          <div className="relative w-full">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.password ? true : undefined}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-2 flex items-center text-[var(--color-mute)] hover:text-[var(--color-ink)]"
            >
              {showPassword ? (
                <EyeSlash aria-hidden="true" size={16} />
              ) : (
                <Eye aria-hidden="true" size={16} />
              )}
            </button>
          </div>
          {fieldErrors.password ? (
            <FieldError>{fieldErrors.password}</FieldError>
          ) : (
            <p className="text-xs text-[var(--color-mute)]">At least 8 characters.</p>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="confirm">Confirm password</FieldLabel>
          <Input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm((event.target as HTMLInputElement).value)}
            aria-invalid={fieldErrors.confirm ? true : undefined}
          />
          {fieldErrors.confirm && <FieldError>{fieldErrors.confirm}</FieldError>}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Resetting…' : 'Reset password'}
        </Button>
      </form>
      {needsCode && (
        <p className="text-center text-sm text-[var(--color-mute)]">
          Need a new code?{' '}
          <Link
            to="/forgot-password"
            className="font-medium text-[var(--color-accent-brand)] hover:underline"
          >
            Start over
          </Link>
        </p>
      )}
    </div>
  );
}
